import { ArrowUpRight, Plus } from 'lucide-react';
import { Button, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { AgentSummary } from '../../../lib/my-agents';
import { Genome } from './genome/Genome';
import styles from './TeamGenomeCard.module.css';

// The "Your Team" panel: the headline count, the two team-level actions, and the genome
// itself (Figma `10261:86168`).
//
// The count is the TEAM — the agents the Maestro leads — so the Maestro is not in it,
// matching the roster below it. `container-type` here is what lets the genome scale
// itself to this column, so it also responds to the skills panel opening.
//
// Neither action is the page's primary CTA. The skills count RECEDES to a ghost button
// with a trailing out-arrow — it opens a panel beside the page, so it reads like the
// destination tiles below rather than a commitment — and Add Agent takes the outlined
// type next to it. The black contained button the earlier frame put on this card is gone:
// nothing on Your Agents claims that weight.

const PAGE = 'your_agents';

interface Props {
  agents: AgentSummary[];
  skillNames: string[];
  skillCount: number;
  maestroName: string;
  maestroId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpenSkills: () => void;
  onAddAgent: () => void;
}

export function TeamGenomeCard({
  agents,
  skillNames,
  skillCount,
  maestroName,
  maestroId,
  selectedId,
  onSelect,
  onOpenSkills,
  onAddAgent,
}: Props) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  return (
    <section className={styles.card} aria-labelledby="agents-team-heading">
      <header className={styles.header}>
        <Typography variant="h1" color="strong" component="h2" id="agents-team-heading" className={styles.title}>
          {t('agents.team')}
          <span className={styles.separator} aria-hidden />
          {agents.length}
        </Typography>
        <div className={styles.actions}>
          <Button
            variant="text"
            endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
            onClick={() => {
              logPageClickBi({ page_name: PAGE, button_name: 'open_skills_panel' });
              onOpenSkills();
            }}
          >
            {t(skillCount === 1 ? 'agents.skillsCountOne' : 'agents.skillsCount', { count: skillCount })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Plus size={16} aria-hidden />}
            onClick={() => {
              logPageClickBi({ page_name: PAGE, button_name: 'add_agent' });
              onAddAgent();
            }}
          >
            {t('agents.addAgent')}
          </Button>
        </div>
      </header>

      <Genome
        agents={agents.map((agent) => ({ id: agent.id || agent.name, name: agent.name }))}
        skillNames={skillNames}
        maestroName={maestroName}
        maestroId={maestroId}
        selectedId={selectedId}
        onSelect={onSelect}
      />
    </section>
  );
}
